import { Routes } from '@datadog/browser-rum-react/react-router-v6';
import { RedirectToLogin, RequiredAuthProvider } from '@propelauth/react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { BrowserRouter, Navigate, Route } from 'react-router-dom';

import 'intro.js/introjs.css';
import 'react-datepicker/dist/react-datepicker.css';

import { EnvVars } from '../environment';
import { UserProvider } from '@/Contexts/User';
import { ErrorBoundary } from '@/new/components/error-boundary';
import { Icon } from '@/new/components/icon';
import { MfaNonSSOGate } from '@/new/components/mfa-gate';
import { RelativityCallback } from '@/new/components/oauth/RelativityCallback';
import { TooltipProvider } from '@/new/components/shadcn/tooltip';
import { Toaster } from '@/new/components/toaster';
import { Admin } from '@/new/pages/admin/organisations';
import { AdminManageOrganisation } from '@/new/pages/admin/organisations/:organisationId';
import { OrganisationRuns } from '@/new/pages/admin/organisations/:organisationId/runs';
import { AdminManageUsers } from '@/new/pages/admin/organisations/:organisationId/users';
import { AllRuns } from '@/new/pages/admin/runs';
import { ClientMaterials } from '@/new/pages/client-materials';
import { Guide } from '@/new/pages/guide';
import { Layout } from '@/new/pages/layout';
import { ManageUsers } from '@/new/pages/manage-users';
import { Matters } from '@/new/pages/matters';
import { MatterOverview } from '@/new/pages/matters/:matterId';
import { DocumentUpload } from '@/new/pages/matters/:matterId/docs/upload';
import { MatterSettings } from '@/new/pages/matters/:matterId/settings';
import { Subsets } from '@/new/pages/matters/:matterId/subsets';
import { SubsetOverview } from '@/new/pages/matters/:matterId/subsets/:subsetId';
import { Chronologies } from '@/new/pages/matters/:matterId/subsets/:subsetId/chronologies';
import { Chronology } from '@/new/pages/matters/:matterId/subsets/:subsetId/chronologies/:chronologyId';
import { DocChronologies } from '@/new/pages/matters/:matterId/subsets/:subsetId/chronologies/doc';
import { DocChronology } from '@/new/pages/matters/:matterId/subsets/:subsetId/chronologies/doc/:docChronologyId';
import { Documents } from '@/new/pages/matters/:matterId/subsets/:subsetId/docs';
import { Document } from '@/new/pages/matters/:matterId/subsets/:subsetId/docs/:docId';
import { SubsetEditor } from '@/new/pages/matters/:matterId/subsets/:subsetId/edit';
import { Entities } from '@/new/pages/matters/:matterId/subsets/:subsetId/entities';
import { Facts } from '@/new/pages/matters/:matterId/subsets/:subsetId/facts';
import { Inconsistencies } from '@/new/pages/matters/:matterId/subsets/:subsetId/inconsistencies';
import { Kim } from '@/new/pages/matters/:matterId/subsets/:subsetId/kim';
import { KimThread } from '@/new/pages/matters/:matterId/subsets/:subsetId/kim/:threadId';
import { NewKim } from '@/new/pages/matters/:matterId/subsets/:subsetId/new-kim';
import { Realtime } from '@/new/pages/matters/:matterId/subsets/:subsetId/realtime';
import { Transcript } from '@/new/pages/matters/:matterId/subsets/:subsetId/realtime/:transcriptId';
import { SubsetCreator } from '@/new/pages/matters/:matterId/subsets/new';
import { MatterCreator } from '@/new/pages/matters/new/:matterId';
import { OrganisationSettings } from '@/new/pages/organisation-settings';
import { FieldMappingsPage } from '@/new/pages/relativity/field-mappings';
import { ReleaseNotes } from '@/new/pages/release-notes';
import { UserSettings } from '@/new/pages/settings';
import { Usage } from '@/new/pages/usage';
import { RedirectToNewFrontend } from '@/utils/redirectToNewFrontend';

const queryClient = new QueryClient();

function App() {
  return (
    <ErrorBoundary>
      <RequiredAuthProvider
        authUrl={EnvVars.AUTH_URL}
        displayWhileLoading={
          <div className="flex h-screen w-screen items-center justify-center">
            <Icon className="lucide--loader-circle animate-spin text-gray-500" />
          </div>
        }
        displayIfLoggedOut={<RedirectToLogin postLoginRedirectUrl={EnvVars.FRONTEND_URL} />}
      >
        <QueryClientProvider client={queryClient}>
          <UserProvider>
            <MfaNonSSOGate>
              <BrowserRouter>
                <RedirectToNewFrontend />
                <TooltipProvider>
                  <Toaster />
                  <Routes>
                    <Route path="oauth/relativity/callback" element={<RelativityCallback />} />

                    <Route path="/" element={<Layout />}>
                      <Route index element={<Navigate to="/matters" replace />} />
                      <Route path="settings" element={<UserSettings />} />
                      <Route path="guide" element={<Guide />} />
                      <Route path="client-materials" element={<ClientMaterials />} />
                      <Route path="release-notes" element={<ReleaseNotes />} />
                      <Route path="usage" element={<Usage />} />
                      <Route path="manage-users" element={<ManageUsers />} />
                      <Route path="organisation-settings/*" element={<OrganisationSettings />} />
                      <Route path="relativity/field-mappings" element={<FieldMappingsPage />} />

                      <Route path="admin">
                        <Route path="runs" element={<AllRuns />} />

                        <Route path="organisations">
                          <Route index element={<Admin />} />

                          <Route path=":organisationId">
                            <Route index element={<AdminManageOrganisation />} />
                            <Route path="users" element={<AdminManageUsers />} />
                            <Route path="runs" element={<OrganisationRuns />} />
                          </Route>
                        </Route>
                      </Route>

                      <Route path="matters">
                        <Route index element={<Matters />} />
                        <Route path="new/:matterId/*" element={<MatterCreator />} />

                        <Route path=":matterId">
                          <Route index element={<MatterOverview />} />

                          <Route path="docs">
                            <Route path="upload" element={<DocumentUpload />} />
                          </Route>

                          <Route path="settings" element={<MatterSettings />}>
                            <Route path="metadata" />
                            <Route path="issues" />
                            <Route path="document-management" />
                            <Route path="relativity-sync" />
                            <Route path="users" />

                            <Route path="file-history">
                              <Route path="overview" />

                              <Route path="file-log">
                                <Route path="uploads/:uploadId" />
                                <Route path="deletions/:deletionId" />
                              </Route>

                              <Route path="run-log">
                                <Route path=":pathId" />
                              </Route>

                              <Route path="skipped" />
                              <Route path="deletions" />
                              <Route path="uploads/:uploadId" />
                              <Route path="deletions/:deletionId" />
                            </Route>

                            <Route path="audit-trail" />
                          </Route>

                          <Route path="subsets">
                            <Route index element={<Subsets />} />
                            <Route path="new" element={<SubsetCreator />} />

                            <Route path=":subsetId">
                              <Route path="edit" element={<SubsetEditor />} />
                              <Route index element={<SubsetOverview />} />
                              <Route path="facts" element={<Facts />} />

                              <Route path="chronologies">
                                <Route index element={<Chronologies />} />
                                <Route path=":chronologyId" element={<Chronology />} />
                                <Route path="doc" element={<DocChronologies />} />
                                <Route path="doc/:docChronologyId" element={<DocChronology />} />
                              </Route>

                              <Route path="docs">
                                <Route index element={<Documents />} />
                                <Route path=":docId" element={<Document />} />
                              </Route>

                              <Route path="inconsistencies" element={<Inconsistencies />} />

                              <Route path="kim">
                                <Route index element={<Kim />} />
                                <Route path=":threadId" element={<KimThread />} />
                              </Route>

                              {/* New Kim */}
                              <Route path="new-kim/*" element={<NewKim />} />

                              <Route path="realtime">
                                <Route index element={<Realtime />} />
                                <Route path=":transcriptId" element={<Transcript />} />
                              </Route>

                              <Route path="entities" element={<Entities />} />
                            </Route>
                          </Route>
                        </Route>
                      </Route>

                      <Route path="*" element={<Navigate to="/matters" replace />} />
                    </Route>
                  </Routes>
                </TooltipProvider>
              </BrowserRouter>
            </MfaNonSSOGate>
          </UserProvider>
        </QueryClientProvider>
      </RequiredAuthProvider>
    </ErrorBoundary>
  );
}

export default App;
